import axios from "axios";

/**
 * BlendedOfferingsService class to handle publishing and scheduling of blended courses.
 */
class BlendedOfferingsService {
  static BASE_URL = process.env.REACT_APP_BASE_URL1;

  /**
   * Fetches course modules by program name for scheduling.
   * @param {string} programName - The name of the program
   * @returns {Promise<Array>} The list of modules for the program
   * @throws Will throw an error if the request fails.
   */
  static async getCourseModulesByProgram(programName) {
    try {
      const response = await axios.get(
        `${BlendedOfferingsService.BASE_URL}/admin/api/blended-courses/modules/${encodeURIComponent(programName)}`
      );
      return response.data;
    } catch (error) {
      console.error("API Error:", error.response || error.message);
      throw new Error(
        error.response?.data?.message || "Failed to communicate with the server."
      );
    }
  }

  /**
   * Publishes a blended course with scheduled connects and assessment dates.
   * @param {Object} payload - The course publishing data
   * @param {string} payload.month - Selected month for the course
   * @param {string} payload.program - Selected program name
   * @param {Array} payload.modules - Array of modules with scheduling information
   * @returns {Promise<Object>} The response data from the server
   * @throws Will throw an error if the request fails.
   */
  static async saveBlendedOffering(payload) {
    try {
      const response = await axios.post(
        `${BlendedOfferingsService.BASE_URL}/admin/api/blended-courses-offerings/saveBlendedOffering`,
        payload
      );
      return response.data;
    } catch (error) {
      console.error("API Error:", error.response || error.message);
      throw new Error(
        error.response?.data?.message || "Failed to communicate with the server."
      );
    }
  }

  /**
   * Deletes an existing blended learning offering from the database.
   * @param {string|number} offeringId - The ID of the blended program offering to delete
   * @returns {Promise<Object>} The response data from the server
   * @throws Will throw an error if the request fails.
   */
  static async deleteExistingOffering(blendedProgramOfferingId) {
    try {
      alert("Delete "+blendedProgramOfferingId);
      const response = await axios.delete(
        `${BlendedOfferingsService.BASE_URL}/admin/api/blended-courses-offerings/deleteBlendedOffering/${blendedProgramOfferingId}`
      );
      return response.data;
    } catch (error) {
      console.error("API Error:", error.response || error.message);
      throw new Error(
        error.response?.data?.message || "Failed to communicate with the server."
      );
    }
  }

    /**
   * Fetches all blended offerings for a specific month.
   * @param {string} month - Month in YYYY-MM format
   * @returns {Promise<Array>} The list of BlendedOfferingBean objects for the month
   * @throws Will throw an error if the request fails.
   */
  static async getProgramsByMonth(track, month) {
    try {
      const response = await axios.get(
        `${BlendedOfferingsService.BASE_URL}/admin/api/blended-courses-offerings/programs/${track}/${month}`
      );
      return response.data;
    } catch (error) {
      console.error("API Error:", error.response || error.message);
      throw new Error(
        error.response?.data?.message || "Failed to communicate with the server."
      );
    }
  }
  /**
   * Fetches all blended offerings for a specific track and month.
   * @param {string} track - User's track
   * @param {string} month - Month in YYYY-MM format
   * @returns {Promise<Array>} The list of BlendedOfferingBean objects for the track and month
   * @throws Will throw an error if the request fails.
   */
  static async getProgramsByTrackAndMonth(track, month) {
    try {
      const response = await axios.get(
        `${BlendedOfferingsService.BASE_URL}/admin/api/blended-courses-offerings/programs/${track}/${month}`
      );
      return response.data;
    } catch (error) {
      console.error("API Error:", error.response || error.message);
      throw new Error(
        error.response?.data?.message || "Failed to communicate with the server."
      );
    }
  }
  /**
   * Opens blended courses for nominations.
   * @param {number} month - Month number (1-12)
   * @param {string} track - Track name
   * @param {Array} blendedOfferingBeans - Array of BlendedOfferingBean objects
   * @returns {Promise<string>} The response message from the server
   * @throws Will throw an error if the request fails.
   */
  static async openNominationsBlendedCourses(yearMonth, track, blendedOfferingBeans) {
    try {
      const response = await axios.post(
        `${BlendedOfferingsService.BASE_URL}/admin/api/blended-courses-offerings/openNominationsBlendedCourses/${yearMonth}/${track}`,
        blendedOfferingBeans,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error("API Error:", error.response || error.message);
      throw new Error(
        error.response?.data || error.response?.data?.message || error.message || "Failed to communicate with the server."
      );
    }
  }
}

export default BlendedOfferingsService;